import type { SettableComponent, TemporalComponent, TimeComponent } from "./types";
import { dateGet, dateTimeGet, isAccepted, settableComponents, type Settings, type ReadOptions } from "./functions";

type ComponentValue<C> = C extends TemporalComponent ? number : string;

// An unknown name would reach the setters as an unknown key, which they ignore — refused here instead
const asComponent = (c: string, value?: string | number): Settings =>
	settableComponents.has(c) ? { [c]: value } : { epoch: NaN };

/**
 * What `theTime` and `theUTCTime` are, and what a `TimeDate` holds to make the next one.
 *
 * @param init: string | number | Date | TimeDate | Settings | null – Date object, date time string, epoch milliseconds, set options, or `null` as absence.
 * @param whereOptions: Settings -- set options set init when init isn't options.
 * @returns TimeDate – extended Date object. */
export type Factory = (init?: Date | TimeDate | Settings | string | number | null, whereOptions?: Settings) => TimeDate;

/** Extended Date object with additional methods. Requires a factory to create new TimeDate objects. */
export class TimeDate extends Date {
	private _new: Factory;
	private _Z?: boolean;

	// `Z` and `init` take an explicit `undefined`, so callers can pass them through unguarded
	constructor(opt: { Z?: boolean | undefined; next: Factory; init?: string | Date | TimeDate | undefined; }) {
		if (opt.init !== undefined) super(opt.init);
		else super();
		this._new = opt.next;
		this._Z = opt.Z || false;
	}
	/** Set component(s) — pass a single name + value or a `Settings` object. Returns a new `TimeDate`. */
	where(c: Settings | SettableComponent = {}, value?: string | number) {
		return this._new(this, typeof c !== "string" ? c : asComponent(c, value));
	}
	/** Add or subtract on component(s) — same shape as `where`, with `add: true` applied. Returns a new `TimeDate`. */
	goes(c: Settings | SettableComponent = {}, value?: string | number) {
		// A kind that cannot be honoured is passed on whole — spreading it would leave only the flag
		return this._new(this, !isAccepted.component(c) ? c
			: { ...(typeof c === "string" ? asComponent(c, value) : c), add: true });
	}
	/** Move to ISO weekday in this' week (1 = Monday, 7 = Sunday). Returns a new `TimeDate`. */
	whereDayOfWeek(weekday: 1 | 2 | 3 | 4 | 5 | 6 | 7) {
		const day = this._Z ? this.getUTCDay() : this.getDay();
		return this._new(this).goes({ DD: weekday - (day || 7) });
	}
	/** Move to last day of this' month. Returns a new `TimeDate`. */
	whereLastDayOfMonth() {
		return this._new(this, { DD: 1 }).goes({ MM: +1 }).where({ DD: 0 });
	}
	/** Get single component. ISO name (`YYYY`, `MM`, ...) → padded string. Temporal name (`year`, `month`, ...) → number. */
	get<C extends TimeComponent | undefined>(
		c?: C,
		options?: ReadOptions,
	): ComponentValue<C> {
		const Z = options?.timeZone ?? options?.Z ?? this._Z;
		// An invalid value has no zone-specific reading, and the Intl path would throw
		// for it — the get-methods report the absence whichever path is asked for.
		return (typeof Z === "string" && this.isValid()
			? dateTimeGet({ component: c || "epoch", timeZone: Z }, this)
			: dateGet({
				...(c ? { component: c } : { component: "epoch" }),
				...(Z ? { UTC: true } : {})
			}, this)) as ComponentValue<C>;
	}
	/** Get component values — polymorphic per element. ISO name → padded string, Temporal name → number. */
	list<const C extends TimeComponent>(c: C | C[], opt?: ReadOptions): ComponentValue<C>[] {
		return (Array.isArray(c) ? c : [c]).map((n) => this.get(n, opt));
	}
	/**
	 * Get joined components string. Format follows name choice — Temporal/number-style,
	 * ISO/padded-string-style. An invalid `TimeDate` renders as the empty string,
	 * whole rather than a skeleton of separators, so `to(…) || fallback` catches it.
	 */
	to(c: TimeComponent | TimeComponent[], y?: string | ReadOptions, z?: ReadOptions): string {
		return !this.isValid() ? ""
			: this.list(c, typeof y === "object" ? y : z).join(typeof y === "string" ? y : "");
	}
	/** Get plain ISO date string (`YYYY-MM-DD`). */
	toPlainDateString(as: ReadOptions = {}): string {
		return this.to(["YYYY", "MM", "DD"], "-", as);
	}
	/** Is `this` in inclusive range? 0 args: not future. 1 arg `start`: in `[start, now]`. 2 args: in `[start, end]`. */
	isInRange(start?: TimeDate | Date, end?: TimeDate | Date): boolean {
		return ((t) => !start
				? t <= Date.now()
				: !end
					? start.getTime() <= t && t <= Date.now()
					: start.getTime() <= t && t <= end.getTime()
		)(this.getTime());
	}
	/** Is `this` before `date` (defaults to now)? */
	isBefore(date?: TimeDate | Date): boolean {
		return this.getTime() < (date ? date.getTime() : Date.now());
	}
	/** Is `this` after `date` (defaults to now)? */
	isAfter(date?: TimeDate | Date): boolean {
		return this.getTime() > (date ? date.getTime() : Date.now());
	}
	/** Is `this` a real moment? `false` when built from input the lib could not honour. */
	isValid(): boolean {
		return !Number.isNaN(this.getTime());
	}
	/**
	 * Returns `this`, or throws `RangeError` if it is invalid. Construction never throws,
	 * so this is how a caller asks for one — at the boundary where the input arrived,
	 * rather than further on wherever a string is first rendered.
	 */
	orThrow(): this {
		if (!this.isValid()) throw new RangeError("Invalid time value");
		return this;
	}
}
